import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Question, QUESTION_TYPE } from 'types/question.types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@epoch-ui/components/Select/select';
import { Button } from '@epoch-ui/components/Button';

import { AggregatedQuestionResponses } from './AggregatedQuestionResponses';
import { PaginatedQuestionResponses } from './PaginatedQuestionResponses';

interface QuestionResponsesProps {
  questions: Question[];
  responsesTotal: number;
}

export const QuestionResponses: React.FC<QuestionResponsesProps> = ({
  questions,
  responsesTotal
}) => {
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>(
    questions[0]?.id?.toString() || ''
  );

  const currentIndex = questions.findIndex((q) => q.id?.toString() === selectedQuestionId);
  const currentQuestion = questions[currentIndex];

  const handlePrevious = (): void => {
    if (currentIndex > 0) {
      setSelectedQuestionId(questions[currentIndex - 1].id?.toString() || '');
    }
  };

  const handleNext = (): void => {
    if (currentIndex < questions.length - 1) {
      setSelectedQuestionId(questions[currentIndex + 1].id?.toString() || '');
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-items-center tw-gap-4">
        <Select value={selectedQuestionId} onValueChange={setSelectedQuestionId}>
          <SelectTrigger className="tw-mr-2 tw-w-[610px] tw-leading-5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {questions.map((question) => (
              <SelectItem key={question.id} value={question.id?.toString() || ''}>
                {question.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Previous question"
          className="tw-p-0"
          onClick={handlePrevious}
          disabled={currentIndex <= 0}
        >
          <ChevronLeft className="tw-h-6 tw-w-6" />
        </Button>
        <div className="tw-leading-5">
          {currentIndex + 1} of {questions.length}
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Next question"
          className="tw-p-0"
          onClick={handleNext}
          disabled={currentIndex >= questions.length - 1}
        >
          <ChevronRight className="tw-h-6 tw-w-6" />
        </Button>
      </div>

      <div>
        {currentQuestion.type === QUESTION_TYPE.LONG ? (
          <PaginatedQuestionResponses
            key={currentQuestion.id}
            question={currentQuestion}
            responsesTotal={responsesTotal}
          />
        ) : (
          <AggregatedQuestionResponses question={currentQuestion} responsesTotal={responsesTotal} />
        )}
      </div>
    </div>
  );
};
